import { EMPTY_SELECT, POInlineEditableField } from '@/components/po-inline-edit'
import {
  MANUFACTURER_RATINGS,
  ratingChipClass,
  ratingLabel,
  type ManufacturerRatingKind,
  type ManufacturerRatingValue,
} from '@/lib/manufacturer-ratings'
import { cn } from '@/lib/utils'

type Value = ManufacturerRatingValue[ManufacturerRatingKind]

/**
 * One rating of a manufacturer in a category as a colored chip that opens a select (USA-1272).
 * A value carried from a parent category shows dashed, named in its tooltip.
 */
export function RatingSelect({
  rating,
  value,
  inherited,
  context,
  pending,
  onChange,
}: {
  rating: ManufacturerRatingKind
  /** Set on this category. */
  value: string | null | undefined
  /** Carried from the nearest parent category that sets it, when this one does not. */
  inherited?: { value: string; from: string } | null
  /** Who and where, for the accessible name: "Curtis in Coffee Brewers". */
  context: string
  pending: boolean
  onChange: (value: string | null) => void
}) {
  const { label, options } = MANUFACTURER_RATINGS[rating]
  const shown = (value ?? inherited?.value ?? null) as Value | null
  const display = shown ? ratingLabel(rating, shown) : 'Not set'
  return (
    <POInlineEditableField
      kind="select"
      value={value ?? null}
      displayValue={display}
      options={[
        ...options.map((option) => ({ value: option.value, label: option.label })),
        { value: EMPTY_SELECT, label: 'Not set' },
      ]}
      pending={pending}
      ariaLabel={`${label} for ${context}: ${display}${!value && inherited ? `, from ${inherited.from}` : ''}. Change`}
      title={!value && inherited ? `From ${inherited.from}` : `Change ${label.toLowerCase()}`}
      buttonClassName={cn(
        'min-h-7 min-w-0 gap-0.5',
        shown
          ? cn(ratingChipClass(rating, shown, !value), 'hover:bg-[inherit] hover:brightness-95')
          : 'text-xs text-muted-foreground',
      )}
      onConfirm={(raw) => onChange(raw || null)}
    />
  )
}
